// Marketplace keyword ranks for one catalog product (issue #217).
//
// This is the tracker table filtered to one SKU, and it is rendered with the
// tracker's own `Table` so it reads as the same object: identical column names,
// identical header hints, ranks right-aligned in narrow columns beside their
// keyword. The previous layout put each row in a three-column grid cell and
// pushed the ranks to the far edge with `justify-between`, which on a wide
// viewport left several hundred pixels between a keyword and its own numbers.

import { Link, useNavigate } from "@tanstack/react-router"
import { ChevronRight, ExternalLink, TrendingUp } from "lucide-react"
import type { SearchProductRankings } from "@nova-ai/shared"

import { Table, type TableColumn } from "../ui/table"
import { QueryState } from "../layout/query-state"
import { useSearchMarketplaces, useSearchProductRankings } from "../../services/use-search-rankings"
import { ORGANIC_RANK_HINT, SPONSORED_RANK_HINT } from "./search-ranking-keyword-detail.derive"

type RankingRow = SearchProductRankings["rankings"][number]

/** One section per configured search marketplace. Which ones exist is the
 * API's answer, so a second marketplace needs no change here. */
export function ProductSearchRankingsSections({ sku }: { sku: string }) {
  const marketplaces = useSearchMarketplaces()
  return (
    <section className="space-y-4" aria-label="Keyword rankings">
      <QueryState
        isLoading={marketplaces.isLoading}
        error={marketplaces.error}
        isEmpty={marketplaces.data?.length === 0}
        emptyTitle="No keyword marketplaces"
        emptyMessage="No marketplace keyword trackers are configured."
      >
        {(marketplaces.data ?? []).map((marketplace) => (
          <ProductSearchRankings key={marketplace.id} marketplace={marketplace.id} sku={sku} />
        ))}
      </QueryState>
    </section>
  )
}

const COLUMNS: readonly TableColumn<RankingRow>[] = [
  {
    key: "phrase",
    header: "Keyword",
    render: (row) => (
      <span className="inline-flex items-center gap-1.5">
        <span className="font-medium">{row.phrase}</span>

        <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/50" />
      </span>
    ),
  },
  {
    key: "organic",
    header: "Organic #",
    align: "right",
    headerHint: ORGANIC_RANK_HINT,
    render: (row) =>
      row.bestOrganicRank === null ? (
        <span className="text-muted-foreground/50">—</span>
      ) : (
        <span className="font-semibold tabular-nums">{`#${row.bestOrganicRank}`}</span>
      ),
  },
  {
    key: "ad",
    header: "Ad #",
    align: "right",
    headerHint: SPONSORED_RANK_HINT,
    render: (row) =>
      row.bestSponsoredRank === null ? (
        <span className="text-muted-foreground/50">—</span>
      ) : (
        <span className="tabular-nums text-muted-foreground">{`#${row.bestSponsoredRank}`}</span>
      ),
  },
]

/** Leads with the answer the page is being asked: how visible is this product in
 *  marketplace search. Counts only what the payload actually carries — the total
 *  number of tracked keywords is not in it, so the phrasing does not imply a
 *  denominator. */
function summarize(rows: readonly RankingRow[]): string {
  const organic = rows.map((row) => row.bestOrganicRank).filter((rank) => rank !== null)
  const sponsored = rows.map((row) => row.bestSponsoredRank).filter((rank) => rank !== null)
  const keywordCount = `${rows.length} tracked keyword${rows.length === 1 ? "" : "s"}`
  const parts = [
    organic.length === 0
      ? "Not on the first page organically"
      : `Best organic #${Math.min(...organic)}`,
    ...(sponsored.length === 0 ? [] : [`best ad #${Math.min(...sponsored)}`]),
    `on the first page for ${keywordCount}`,
  ]
  return parts.join(" · ")
}

function ProductSearchRankings({ marketplace, sku }: { marketplace: string; sku: string }) {
  const navigate = useNavigate()
  const rankings = useSearchProductRankings(marketplace, sku)

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <QueryState
        isLoading={rankings.isLoading}
        error={rankings.error}
        isEmpty={rankings.data?.matches.length === 0}
        emptyTitle="No ranking match"
        emptyMessage={`No tracked keyword listing is matched to this product on ${marketplace}.`}
        embedded
      >
        {rankings.data ? (
          <ProductSearchRankingsContent
            data={rankings.data}
            onOpenKeyword={(phrase) => {
              void navigate({
                to: "/keywords/$marketplace/$phrase",
                params: { marketplace: rankings.data?.marketplace.id ?? marketplace, phrase },
              })
            }}
          />
        ) : null}
      </QueryState>
    </section>
  )
}

function ProductSearchRankingsContent({
  data,
  onOpenKeyword,
}: {
  data: SearchProductRankings
  onOpenKeyword: (phrase: string) => void
}) {
  const { marketplace: metadata, matches, rankings: rows } = data
  return (
    <div className="px-5 py-4">
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 pb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="size-4 shrink-0 text-muted-foreground" />

            <h3 className="text-sm font-semibold">{metadata.name} · keyword positions</h3>
          </div>

          {rows.length === 0 ? null : (
            <p className="mt-1 text-xs text-muted-foreground">{summarize(rows)}</p>
          )}

          {/* The matched listings were previously only a `title` tooltip, which
              hid the one thing that explains where these ranks came from. */}
          <ul className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
            {matches.map((match) => (
              <li key={match.externalProductKey} className="min-w-0">
                <a
                  href={match.url}
                  target="_blank"
                  rel="noreferrer"
                  title={match.name}
                  className="inline-flex max-w-[22rem] items-center gap-1 rounded-full bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                >
                  <span className="truncate">{match.name}</span>

                  <ExternalLink className="size-2.5 shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        </div>

        <Link
          to="/keywords/$marketplace"
          params={{ marketplace: metadata.id }}
          className="shrink-0 text-xs font-medium text-info hover:underline"
        >
          Open tracker
        </Link>
      </header>

      {rows.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">
          The matched listings are not on the first page of any tracked keyword today.
        </p>
      ) : (
        <Table
          columns={COLUMNS}
          rows={rows}
          rowKey={(row) => row.phrase}
          cellAlign="middle"
          dense
          embedded
          className="-mx-5 border-x-0 border-b-0 border-t border-border shadow-none"
          rowSeparator={(current, previous) =>
            previous?.category !== current.category ? current.category : null
          }
          onRowClick={(row) => onOpenKeyword(row.phrase)}
        />
      )}
    </div>
  )
}
